import type { TextInputProps } from '@meterup/atto';
import {
  CompositeField,
  FieldContainer,
  Icon,
  MultiComboBox,
  MultiComboBoxItem,
  Select,
  SelectItem,
  styled,
  TextInput,
} from '@meterup/atto';
import { isNumber } from 'lodash-es';
import { type Key, type ReactNode, useCallback, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

type CommonFilterProps = {
  id: string;
  label: string;
};

type TextFilterProps = CommonFilterProps & {
  value: string;
  onChange: (id: string, value: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

type SelectFilterProps = CommonFilterProps & {
  value: string;
  options: { value: string; label: ReactNode }[];
  handleFiltersChange: (id: string, key: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

type MultiSelectFilterProps = CommonFilterProps & {
  value: string[];
  options: { value: string; label: ReactNode }[];
  handleFiltersChange: (id: string, values: string[]) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

export const TableFiltersFields = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$4',
  padding: '$6 $8',
  width: '100%',
});
TableFiltersFields.displayName = 'TableFiltersFields';

export function TableSelectFilter({
  id,
  label,
  value,
  handleFiltersChange,
  options,
  onKeyDown,
}: SelectFilterProps) {
  if (options) {
    return (
      <FieldContainer background="application">
        <CompositeField
          label={label}
          element={
            <TableFiltersFields>
              <Select
                popoverProps={{ className: `disableOutsideClick_${id}` }}
                aria-label={label}
                value={value}
                onValueChange={(key: string) => handleFiltersChange(id, key)}
                size="medium"
                width="100%"
                onKeyDown={onKeyDown}
              >
                {options.map((option) => (
                  <SelectItem
                    key={option.value}
                    textValue={typeof option.label === 'string' ? option.label : option.value}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </Select>
            </TableFiltersFields>
          }
        />
      </FieldContainer>
    );
  }
}

export function TableMultiSelectFilter({
  id,
  label,
  value,
  handleFiltersChange,
  options,
  onKeyDown,
}: MultiSelectFilterProps) {
  if (options) {
    return (
      <FieldContainer background="application">
        <CompositeField
          label={label}
          element={
            <TableFiltersFields>
              <MultiComboBox
                className={`disableOutsideClick_${id}`}
                onKeyDown={onKeyDown}
                aria-label={label}
                value={value}
                onValueChange={(keys: 'all' | Set<Key>) =>
                  handleFiltersChange(
                    id,
                    keys === 'all' ? [] : Array.from(keys).map((k) => k.toString()),
                  )
                }
                width="100%"
                enableSelectAll
              >
                {options.map((option) => (
                  <MultiComboBoxItem
                    key={option.value}
                    textValue={typeof option.label === 'string' ? option.label : option.value}
                  >
                    {option.label}
                  </MultiComboBoxItem>
                ))}
              </MultiComboBox>
            </TableFiltersFields>
          }
        />
      </FieldContainer>
    );
  }
}

export function TableFiltersDebouncedInput({
  id,
  value,
  label,
  onChange,
  onKeyDown,
  parseValue,
  ...restProps
}: Omit<TextInputProps, 'onChange'> & {
  id: string;
  onChange: (id: string, value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  parseValue?: (value: string) => string;
}) {
  const [localVal, setLocalVal] = useState(value ?? '');
  const debouncedCallback = useDebouncedCallback((val) => {
    onChange(id, val);
  }, 250);

  const handleOnChange = useCallback(
    (val: string) => {
      const parsedValue = parseValue ? parseValue(val) : val;
      setLocalVal(parsedValue);
      debouncedCallback(parsedValue);
    },
    [setLocalVal, debouncedCallback, parseValue],
  );
  return (
    <CompositeField
      label={label}
      element={
        <TableFiltersFields>
          <TextInput
            {...restProps}
            id={id}
            onChange={handleOnChange}
            onKeyDown={onKeyDown}
            value={localVal}
            size="small"
            width="100%"
          />
        </TableFiltersFields>
      }
    />
  );
}

export function TableTextFilter({ id, value, label, onChange, onKeyDown }: TextFilterProps) {
  return (
    <FieldContainer background="application">
      <CompositeField
        label={label}
        element={
          <TableFiltersFields>
            <TableFiltersDebouncedInput
              id={id}
              label={label}
              onChange={onChange}
              onKeyDown={onKeyDown}
              value={value}
            />
          </TableFiltersFields>
        }
      />
    </FieldContainer>
  );
}

const NumberRangeFieldsIcon = styled(Icon, {
  width: '$12',
  height: '$12',
});
NumberRangeFieldsIcon.displayName = 'NumberRangeFieldsIcon';

export function TableNumberFilter({
  id,
  value,
  label,
  handleFiltersChange,
  onKeyDown,
}: {
  id: string;
  value: number | null;
  label: string;
  handleFiltersChange: (id: string, value: number | null) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}) {
  return (
    <FieldContainer background="application">
      <CompositeField
        label={label}
        element={
          <TableFiltersFields>
            <TableFiltersDebouncedInput
              id={id}
              label={label}
              onChange={(inputId, key) => {
                const num = Number(key);
                const val = key === '' ? null : num;
                handleFiltersChange(inputId, val);
              }}
              onKeyDown={onKeyDown}
              value={value === null ? '' : String(value)}
              type="number"
            />
          </TableFiltersFields>
        }
      />
    </FieldContainer>
  );
}

export function TableNumberRangeFilter({
  id: idProp,
  value,
  label,
  handleFiltersChange,
  onKeyDown,
}: {
  id: string;
  value: [number | null, number | null];
  label: string;
  handleFiltersChange: (id: string, value: [number | null, number | null]) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}) {
  const isInvalidRange = isNumber(value[0]) && isNumber(value[1]) && value[0] > value[1];
  return (
    <FieldContainer background="application">
      <CompositeField
        errorMessage={isInvalidRange ? 'Minimum cannot be above the maximum.' : undefined}
        label="Number range"
        element={
          <TableFiltersFields>
            <CompositeField
              label="Min"
              element={
                <TableFiltersDebouncedInput
                  id={`${idProp}-min`}
                  label={`${label} min.`}
                  onChange={(id, key) => {
                    const min = Number(key);
                    const max = value[1];
                    const val = key === '' ? null : min;
                    handleFiltersChange(idProp, [val, max]);
                  }}
                  onKeyDown={onKeyDown}
                  value={String(value[0])}
                  type="number"
                />
              }
            />
            <NumberRangeFieldsIcon icon="arrow-right" />
            <CompositeField
              label="Max"
              element={
                <TableFiltersDebouncedInput
                  id={`${idProp}-max`}
                  label={`${label} max.`}
                  onChange={(id, key) => {
                    const min = value[0];
                    const max = Number(key);
                    const val = key === '' ? null : max;
                    handleFiltersChange(idProp, [min, val]);
                  }}
                  onKeyDown={onKeyDown}
                  value={String(value[1])}
                  type="number"
                />
              }
            />
          </TableFiltersFields>
        }
      />
    </FieldContainer>
  );
}
